import React, { Dispatch, useMemo, useState } from 'react';

import {
    RatchetDefinitions,
    RatchetDefinitionsEnum,
    RatchetModelDefinitionEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';

import { isUndefined, sortBy } from 'lodash';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { formatDefinitionPercentageValue } from 'src/util/RateCards';

import { TerritoryFlag } from '../TerritoryFlag';

interface IAddConversionModelDefinitionsTable {
    ratchetDefinitions: RatchetDefinitions | undefined;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
}

export const AddConversionModelDefinitionsTable: React.FC<IAddConversionModelDefinitionsTable> = ({
    ratchetDefinitions,
    dispatchRateCardValues
}) => {
    const [expandedRows, setExpandedRows] = useState(new Set<string>());

    const ratchetModelDefinitionsGroupedByType = useMemo(() => {
        const definitionsMap = new Map<string, RatchetDefinitions>();

        ratchetDefinitions?.forEach((definition) => {
            // SKip WEIGHTS and unused TERRITORY CAP fields
            if (
                !isUndefined(definition) &&
                definition.type !== RatchetDefinitionsEnum.WEIGHTS &&
                definition.type !== RatchetDefinitionsEnum.TERRITORY_CAP_DEDUCTION_RATE &&
                definition.type !== RatchetDefinitionsEnum.TERRITORY_CAP &&
                definition.type !== RatchetDefinitionsEnum.MINIMUM_TERRITORIES_BELOW_THRESHOLD
            ) {
                const definitionList = definitionsMap.get(definition.type!) ?? [];
                definitionsMap.set(definition.type!, definitionList.concat(definition));
            }
        });

        return Object.fromEntries(definitionsMap);
    }, [ratchetDefinitions]);

    const handleUmgGuardrailChange = (shouldBeToggled: boolean): void =>
        dispatchRateCardValues({
            type: 'set_umg_guardrail',
            shouldBeToggled
        });

    const rows: JSX.Element[] = [];

    // Header row
    rows.push(
        <TableRow>
            <TableCell>Definition</TableCell>
            <TableCell>Value</TableCell>
        </TableRow>
    );

    Object.entries(ratchetModelDefinitionsGroupedByType).forEach(([type, definitions]) => {
        // Non expandable rows
        if (definitions.length === 1) {
            // Split the type string by underscore
            const parts = type.split('_');

            // If there are multiple parts, create a formatted display name
            const displayName =
                parts.length > 2 ? `${parts.slice(0, 2).join('_')}_\n${parts.slice(2).join('_')}` : type;

            rows.push(
                <TableRow key={type}>
                    <TableCell>
                        <span style={{ whiteSpace: 'pre-line' }}>{displayName}</span>
                    </TableCell>
                    <TableCell>{formatDefinitionPercentageValue(definitions[0].value ?? '-', type)}</TableCell>
                </TableRow>
            );
        }
        // Create global toggle for UMG guardrail
        else if (type === RatchetModelDefinitionEnum.UMG_CONVERSION_GUARDRAIL) {
            rows.push(
                <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell>
                        <Toggle
                            data-testid={`${type.toLowerCase()}-toggle`}
                            checked={definitions.some((definition) => definition.value === 'true')}
                            onChange={handleUmgGuardrailChange}
                        />
                    </TableCell>
                </TableRow>
            );
        } else {
            // Create expandable rows
            rows.push(
                <TableRow key={type}>
                    <TableCell rowSpan={expandedRows.has(type) ? definitions.length + 1 : 1}>{type}</TableCell>
                    <TableCell>
                        <Button
                            type="icon"
                            data-testid={`${type.toLowerCase()}-toggle`}
                            onClick={() => {
                                const isExpanded = expandedRows.has(type);
                                const newExpandedRows = new Set(expandedRows);
                                if (isExpanded) {
                                    newExpandedRows.delete(type);
                                } else {
                                    newExpandedRows.add(type);
                                }
                                setExpandedRows(newExpandedRows);
                            }}
                        >
                            {expandedRows.has(type) ? (
                                <Icon tokens={chevronDownSmallTokens} />
                            ) : (
                                <Icon tokens={chevronRightSmallTokens} />
                            )}
                            Multiple
                        </Button>
                    </TableCell>
                </TableRow>
            );

            // If expanded section is toggled, then display rows
            if (expandedRows.has(type)) {
                const definitionsSortedByTerritory = sortBy(definitions, ['territory']);
                definitionsSortedByTerritory.forEach((definition, index) => {
                    rows.push(
                        <TableRow key={`${type}-${index}`}>
                            <TableCell>
                                <Row widths={['50%', '50%']}>
                                    <TerritoryFlag territoryCode={definition.territory!} showTerritoryName={false} />
                                    <Text>{definition.value}</Text>
                                </Row>
                            </TableCell>
                        </TableRow>
                    );
                });
            }
        }
    });

    return (
        <Column spacing="300" width="100%">
            <Text type="h300">Model definitions</Text>
            <Table headerRows={1} showStripes={false} showDividers={true}>
                {rows.map((row) => row)}
            </Table>
        </Column>
    );
};
